import type { Mark, TTextLeaf } from '@/textLeaf';
import type { Author, PostHeaderData } from '../../types';

interface GetAuthorNamesTextProps {
  authors: Author[];
  authorUrls?: PostHeaderData['author_urls'];
  truncateList: boolean;
  byLineText: string;
  textStyleAttrs?: Record<string, unknown>;
}

export const getAuthorNamesText = ({ authors, truncateList, byLineText }: GetAuthorNamesTextProps) => {
  const authorCount = authors.length;
  const firstAuthorName = authors[0]?.name;

  const hasMultipleAuthors = authorCount > 1;

  const multipleAuthorsText = truncateList
    ? `${firstAuthorName}, +${authors.length - 1}`
    : authors.map((author) => author.name).join(', ');

  const authorsText = hasMultipleAuthors ? `${multipleAuthorsText}` : firstAuthorName;

  return byLineText ? `${byLineText} ${authorsText}` : authorsText;
};

const getLinkMark = (authorUrl: string): Mark => {
  return {
    type: 'link',
    attrs: {
      rel: 'noopener noreferrer',
      href: authorUrl,
      target: '_blank',
      hasUnderline: true,
    },
  };
};

export const getAuthorNamesTextNodes = ({
  authors,
  authorUrls,
  truncateList,
  byLineText,
  textStyleAttrs,
}: GetAuthorNamesTextProps): TTextLeaf[] => {
  if (!authorUrls || Object.keys(authorUrls).length === 0) {
    return [
      {
        text: getAuthorNamesText({
          authors: authors ?? [],
          truncateList,
          byLineText,
        }),
        type: 'text',
        marks: [
          {
            type: 'textStyle',
            attrs: textStyleAttrs,
          },
        ],
      } as TTextLeaf,
    ];
  }

  const byLineTextNode = byLineText
    ? ({
        text: byLineText,
        type: 'text',
        marks: [
          {
            type: 'textStyle',
            attrs: textStyleAttrs,
          },
        ],
      } as TTextLeaf)
    : null;

  const textNodes: TTextLeaf[] = [];

  if (byLineTextNode) {
    textNodes.push(byLineTextNode);
  }

  const textStyleMark: Mark = {
    type: 'textStyle',
    attrs: textStyleAttrs,
  };

  if (!truncateList) {
    authors.forEach((author, index) => {
      const marks: Mark[] = [textStyleMark];

      if (authorUrls[author.id]) {
        marks.unshift(getLinkMark(authorUrls[author.id]));
      }

      textNodes.push({
        text: author.name,
        type: 'text',
        marks,
      } as TTextLeaf);

      if (index === authors.length - 2) {
        textNodes.push({
          text: '&',
          type: 'text',
          marks: [textStyleMark],
        } as TTextLeaf);
      } else if (index < authors.length - 2) {
        textNodes.push({
          text: ',',
          type: 'text',
          marks: [textStyleMark],
        } as TTextLeaf);
      }
    });
  } else {
    const author = authors[0];
    const marks: Mark[] = [textStyleMark];

    if (authorUrls[author.id]) {
      marks.unshift(getLinkMark(authorUrls[author.id]));
    }

    textNodes.push({
      text: author.name,
      type: 'text',
      marks,
    } as TTextLeaf);

    if (authors.length > 1) {
      textNodes.push({
        text: `, +${authors.length - 1}`,
        type: 'text',
        marks: [textStyleMark],
      } as TTextLeaf);
    }
  }

  return textNodes;
};
